import { afterNextRender, Component, DestroyRef, inject, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router, RouterLink } from '@angular/router';
import { DashboardService } from '../../_services/dashboard/dashboard.service';

@Component({
  selector: 'app-for-nris',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink, RouterModule, ReactiveFormsModule
],
  templateUrl: './for-nris.component.html',
  styleUrl: './for-nris.component.scss'
})
export class ForNRIsComponent implements OnInit {

  selectedArticle: any = null;
  expertForm!: FormGroup;
  isScrolled = false;
  @ViewChild('statsSection')
  statsSection!: ElementRef;

  private readonly destroyRef = inject(DestroyRef);

  openArticle(article: any) {
    this.selectedArticle = article;
  }

  closeArticle() {
    this.selectedArticle = null;
  }

  constructor(
    private _DS: DashboardService,
    private router: Router,
    private formBuilder: FormBuilder,
  ) {
    this.expertForm = this.formBuilder.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      message: ['', Validators.required],
    });
    afterNextRender(() => {
      const section = this.statsSection?.nativeElement;
      if (!section || typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) {
          this.stats.forEach(stat => stat.current = stat.target);
          observer.disconnect();
        }
      }, { threshold: .2 });
      observer.observe(section);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  ngOnInit(): void {}

  // ------------------------------------------------
  // TRUST LOGOS
  // ------------------------------------------------

  logos: string[] = [
    'assets/img/logos/ub.png',
    'assets/img/logos/lightspeed.png',
    'assets/img/logos/ola.png',
    'assets/img/logos/bb.png',
    'assets/img/logos/linde.png',
    'assets/img/logos/infosys.png',
    'assets/img/logos/nvidia.png',
    'assets/img/logos/wipro.png',
    'assets/img/logos/peak.png',
    'assets/img/logos/techni.png',
    'assets/img/logos/jsa.png',
    'assets/img/logos/ujjivan.png',
    'assets/img/logos/itc.png',
    'assets/img/logos/tally.png',
    'assets/img/logos/bcg.png',
    'assets/img/logos/360.png',
    'assets/img/logos/kearney.png'
  ];


  // ------------------------------------------------
  // WHY BENGALURU
  // ------------------------------------------------

  stats = [
    {
      target: 24,
      suffix: '%',
      current: 0,
      title: 'Luxury Price Growth',
      desc: 'Year-on-year residential growth'
    },
    {
      target: 41,
      suffix: '%',
      current: 0,
      title: 'GCC Leasing Share',
      desc: 'Of India GCC office leasing'
    },
    {
      target: 9.2,
      suffix: 'M',
      current: 0,
      title: 'Office Transactions',
      desc: 'Sq.ft office space transacted'
    },
    {
      target: 44.47,
      suffix: 'M',
      current: 0,
      title: 'Airport Passengers',
      desc: 'Annual passenger traffic'
    }
  ];

  // ------------------------------------------------
  // GOALS
  // ------------------------------------------------

  goals = [
    {
      title: 'Investment Property',
      image: 'assets/img/inv_prop.jpg',
      desc: 'Build long-term wealth through real estate.',
      desc2: 'Capital appreciation + rental yield. Focus on Hebbal, Whitefield, and North Bengaluru where tech corridors and infrastructure converge.'
    },
    {
      title: 'Returning to India',
      image: 'assets/img/rt_ind.jpg',
      desc: 'Plan your relocation with confidence.',
      desc2: 'End-to-end advisory for relocating families: schooling, healthcare, community living, and homes that feel global.'
    },
    {
      title: 'Family Home',
      image: 'assets/img/fam_hm.jpg',
      desc: 'Find homes near schools and hospitals.',
      desc2: 'Indiranagar, Koramangala, Sadahalli — homes within 15 minutes of TISB, Inventure, Stonehill and Canadian International.'
    },
    {
      title: 'Rental Income',
      image: 'assets/img/mon.jpg',
      desc: 'Generate predictable rental returns.',
      desc2: 'Curated pre-launch access, REIT-grade assets, and developer relationships unavailable in the open market.'
    },
    {
      title: 'Airport Connectivity',
      image: 'assets/img/transp.jpg',
      desc: 'Ideal for globally mobile families.',
      desc2: 'Hebbal, Jakkur and North Bengaluru deliver airport access in under 35 minutes — ideal for global executives.'
    },
    {
      title: 'Future Relocation',
      image: 'assets/img/reloc_2',
      desc: 'Secure your Bengaluru base today.',
      desc2: 'Capital appreciation + rental yield. Focus on Hebbal, Whitefield, and North Bengaluru where tech corridors and infrastructure converge.'
    }
  ];

  selectedGoal: any = null;

  selectGoal(goal: any) {
    this.selectedGoal = goal;
  }

  // ------------------------------------------------
  // MICRO MARKETS
  // ------------------------------------------------

  microMarkets = [
    {
      name: 'Central Bengaluru',
      image: 'assets/img/cb.png',
      rental: 'High',
      airport: '45 mins',
      family: 'Excellent',
      investment: 'Stable',
      description:
        'Legacy neighbourhoods with scarcity value and prestige.',
      bestFor: 'Airport Connectivity'
    },
    {
      name: 'Indiranagar',
      image: 'assets/img/indira_b.jpg',
      rental: 'Very High',
      airport: '40 mins',
      family: 'Excellent',
      investment: 'Premium',
      description:
        'Lifestyle-driven location with strong end-user demand.',
      bestFor: 'Airport Connectivity'
    },
    {
      name: 'Koramangala',
      image: 'assets/img/korm_b.webp',
      rental: 'Very High',
      airport: '50 mins',
      family: 'Strong',
      investment: 'Strong',
      description:
        'Startup ecosystem, premium living and social infrastructure.',
      bestFor: 'Airport Connectivity'
    },
    {
      name: 'Whitefield',
      image: 'assets/img/wb.jpg',
      rental: 'High',
      airport: '50 mins',
      family: 'Excellent',
      investment: 'High Growth',
      description:
        'Technology corridor with strong rental demand.',
      bestFor: 'Airport Connectivity'
    },
    {
      name: 'Hebbal',
      image: 'assets/img/heb_b.jpg',
      rental: 'High',
      airport: '25 Minutes',
      family: 'Excellent',
      investment: 'Top Tier',
      description: 'Luxury developments, strong infrastructure and exceptional airport access.',
      bestFor: 'Airport Connectivity'
    },
    {
      name: 'North Bengaluru',
      image: 'assets/img/nb.png',
      rental: 'Growing',
      airport: '15 mins',
      family: 'Strong',
      investment: 'Highest Upside',
      description:
        'Infrastructure-led growth and future appreciation.',
      bestFor: 'Airport Connectivity'
    }
  ];

  selectedMarket = this.microMarkets[0];

  // ------------------------------------------------
  // BUYING JOURNEY
  // ------------------------------------------------

  steps = [
    {
      id: '01',
      title: 'Share Requirements',
      desc: 'Tell us your budget, location and goals.'
    },
    {
      id: '02',
      title: 'Curated Shortlist',
      desc: 'Receive handpicked opportunities.'
    },
    {
      id: '03',
      title: 'Virtual Review',
      desc: 'Walkthroughs, videos and advisor insights.'
    },
    {
      id: '04',
      title: 'Visit Planning',
      desc: 'Efficient property visits when in India.'
    },
    {
      id: '05',
      title: 'Transaction Support',
      desc: 'Documentation and closing assistance.'
    }
  ];

  // ------------------------------------------------
  // ARTICLES
  // ------------------------------------------------

  articles = [
    {
      title: 'The Global Indian’s Guide to Buying Luxury Property in Bengaluru',
      category: 'Buying Guide',
      image: 'assets/img/article1.jpg',
      desc: 'A complete guide for NRIs buying luxury homes.',
      content: `
        <h3>Overview</h3>
        <p>
          For Global Indians, buying a luxury home in Bengaluru is no longer a someday-return decision. It is a way to stay financially, emotionally and strategically connected to a city shaping India’s wealth story.
        </p>
        <p>
          For NRIs and OCIs based in the US, UK, Gulf, Singapore, Canada or Australia, Bengaluru offers Indian ownership, global-quality neighbourhoods, resilient rental demand and long-term relevance across work, family, education and lifestyle.</br>
          Can NRIs buy property in India? In most cases, Indian citizens living overseas and OCI cardholders can buy residential and commercial property in India. The main exclusions are agricultural land, plantation property and farmhouses, unless allowed through inheritance or approval. Payments are usually routed through NRE, NRO or FCNR(B) accounts. Legal, FEMA and tax advice should be taken before committing capital. </br>
          But the real question is not just “can you buy?” It is “what should you buy?” </br>
          Central Bengaluru offers scarcity and legacy value. Indiranagar and Koramangala bring lifestyle and founder networks. Hebbal, Jakkur and North Bengaluru lean into airport connectivity and future infrastructure. Whitefield, Old Airport Road and East Bengaluru appeal to tech-led wealth and family demand.
          A ₹5 crore, ₹10 crore or ₹20 crore home is not just a floor plan. It is a micro-market, builder, title, resale and family-trust decision.</br>
          CircledPlus gives serious buyers curated luxury homes in Bengaluru through a private, preference-led discovery process.</br>
          Not everything needs to be seen.</br>
          Only the right homes do.
        </p>
      `
    },
    {
      category: 'Buying Guide',
      image: 'assets/img/article2.jpg',
      title: 'Buying a Bengaluru Home from Abroad: How NRIs Can Shortlist with Confidence',
      desc: 'How to confidently shortlist remotely.',
      content: `
      <p>
        Buying a Bengaluru home from abroad is no longer a rare NRI decision. It is becoming a structured wealth, family and lifestyle decision for Global Indians based in the UAE, Gulf, US, Canada, UK, Singapore and Australia. </br>
        The reason is simple: Bengaluru’s residential market is moving fast.
      </p>
      <p>
        In Q1 2026, Bengaluru recorded one of India’s strongest housing price movements, with average residential prices rising about 24% year-on-year to nearly ₹9,785 per sq ft. At the same time, the city continues to benefit from office demand, technology-led employment and Global Capability Centre expansion, with Bengaluru capturing a major share of GCC leasing activity.</br>
        For an overseas buyer, this creates opportunity and pressure. The right home may appreciate, rent well and serve as a future India base. The wrong shortlist can waste months and expose the buyer to poor-quality inventory.</br>
        That is why the first step is not browsing. It is intent mapping.</br>
        Is the purchase for relocation, parents, children’s education, rental income, portfolio diversification or long-term family use? A ₹5 crore, ₹10 crore or ₹20 crore home in Indiranagar, Koramangala, CBD, Hebbal, Jakkur, Whitefield or Old Airport Road can serve very different goals.</br>
        Advisor-led shortlisting should filter by micro-market strength, builder reputation, floor plan, privacy, legal readiness, resale potential, rental demand and maintenance quality.</br>
        CircledPlus reduces broker noise through curated access, preference-led discovery and advisory-backed shortlisting.</br>
        Confidence does not come from seeing more homes.</br>
        It comes from seeing the right homes, with the right context.
      </p>
      
      `
    },
    {
      category: 'Location Guide',
      image: 'assets/img/article3.jpg',
      title: 'Bengaluru Micro-Markets for NRIs: Where to Buy Based on Your Goal',
      desc: 'Where to buy based on your goals.',
      content: `
        <p>
          For NRIs buying luxury property in Bengaluru, the key question is not just “Can I buy?” It is “Where should I buy?” </br>
          Bengaluru is not one property market. It is a collection of micro-markets shaped by work demand, neighbourhood culture, infrastructure, rental depth, school access, airport connectivity and scarcity.</br>
          The data shows why this matters now. In Q1 2026, Bengaluru recorded one of India’s strongest residential performances, with average housing prices around ₹9,785 per sq ft and year-on-year price growth of about 24%. High-end and luxury launches formed 68% of new residential launch activity. East Bengaluru accounted for 57% of quarterly launches, while North Bengaluru contributed 38%.</br>
        </p>

        <section class="py-20">

          <div class="max-w-[1440px] mx-auto px-6">

            <div class="max-w-3xl mb-14">

              <p class="text-xs uppercase tracking-[3px] font-semibold text-[#8C52FF]">
                LOCATION MATCHING
              </p>

              <h2 class="mt-4 text-4xl md:text-5xl leading-tight">
                Which Bengaluru micro-market
                matches your goal?
              </h2>

              <p class="mt-5 text-lg text-[#595959]">
                Different neighbourhoods serve different buyer priorities.
                Here's a quick guide to where most NRI buyers begin their search.
              </p>

            </div>

            <div class="space-y-5">

              <div
                class="grid lg:grid-cols-[0.9fr_1.1fr_1fr] gap-6 p-6 rounded-[28px] bg-white border border-gray-100 hover:shadow-lg transition">

                <div>
                  <p class="text-xs uppercase tracking-[2px] text-[#8C52FF] mb-2">
                    Buying Goal
                  </p>
                  <h3 class="text-xl font-semibold">
                    Scarcity & Prestige
                  </h3>
                </div>

                <div>
                  <p class="text-xs uppercase tracking-[2px] text-[#8C52FF] mb-2">
                    Best Locations
                  </p>
                  <p>
                    Central Bengaluru, CBD, Lavelle Road,
                    Cunningham Road, Richmond Road
                  </p>
                </div>

                <div>
                  <p class="text-xs uppercase tracking-[2px] text-[#8C52FF] mb-2">
                    Why It Works
                  </p>
                  <p class="text-[#595959]">
                    Limited supply, legacy addresses and long-term scarcity value.
                  </p>
                </div>

              </div>

              <div
                class="grid lg:grid-cols-[0.9fr_1.1fr_1fr] gap-6 p-6 rounded-[28px] bg-white border border-gray-100 hover:shadow-lg transition">

                <div>
                  <h3 class="text-xl font-semibold">
                    Lifestyle & City Access
                  </h3>
                </div>

                <div>
                  <p>
                    Indiranagar, Koramangala,
                    Old Airport Road
                  </p>
                </div>

                <div>
                  <p class="text-[#595959]">
                    Mature neighbourhoods, social infrastructure and strong end-user demand.
                  </p>
                </div>

              </div>
              <div
                class="grid lg:grid-cols-[0.9fr_1.1fr_1fr] gap-6 p-6 rounded-[28px] bg-white border border-gray-100 hover:shadow-lg transition">

                <div>
                  <h3 class="text-xl font-semibold">
                    Tech-Family Living
                  </h3>
                </div>

                <div>
                  <p>
                    Whitefield, ORR, East Bengaluru
                  </p>
                </div>

                <div>
                  <p class="text-[#595959]">
                    Technology hubs, international schools and established communities.
                  </p>
                </div>

              </div>

              <div
                class="grid lg:grid-cols-[0.9fr_1.1fr_1fr] gap-6 p-6 rounded-[28px] bg-white border border-gray-100 hover:shadow-lg transition">

                <div>
                  <h3 class="text-xl font-semibold">
                    Airport & Future Growth
                  </h3>
                </div>

                <div>
                  <p>
                    Hebbal, Jakkur,
                    Devanahalli, North Bengaluru
                  </p>
                </div>

                <div>
                  <p class="text-[#595959]">
                    Airport access, larger formats and infrastructure-led appreciation.
                  </p>
                </div>

              </div>

              <div
                class="grid lg:grid-cols-[0.9fr_1.1fr_1fr] gap-6 p-6 rounded-[28px] bg-white border border-gray-100 hover:shadow-lg transition">

                <div>
                  <h3 class="text-xl font-semibold">
                    Rental & Corporate Demand
                  </h3>
                </div>

                <div>
                  <p>
                    Whitefield, ORR,
                    Hebbal, Old Airport Road
                  </p>
                </div>

                <div>
                  <p class="text-[#595959]">
                    Strong GCC presence and demand from senior professionals.
                  </p>
                </div>

              </div>

            </div>

          </div>

        </section>

        <p>
          A ₹10 crore home in Central Bengaluru, Indiranagar, Whitefield or Hebbal can represent four different decisions: scarcity, lifestyle, rental depth or future growth. </br>
          CircledPlus helps serious NRI buyers shortlist homes based on lifestyle, budget, micro-market fit and long-term intent.</br>
          The best Bengaluru home is the one that fits the reason you are buying.</br>
        </p>
      `
    },
    {
      category: 'Lifestyle',
      image: 'assets/img/article4.jpg',
      title: 'Returning to Bengaluru? How NRIs Should Choose the Right Home Base',
      desc: 'Planning your next chapter in India.',
      content: `
        <p>
          For many NRIs, buying a home in Bengaluru is not just about investment. It is about coming back with clarity. </br>
          For families based in Australia, the UK, US, Canada, Singapore or the Gulf, the idea of returning to Bengaluru may begin as a retirement plan, a future base for children, a home for ageing parents, or a deeper connection to India. But when the decision becomes real, the question changes from “Which property should I buy?” to “Where can my family actually live well?” </br>
          Bengaluru’s growth is no longer limited to technology parks. In Q1 2026, the city recorded 9.2 million sq ft of office transactions and accounted for more than 30% of office space transacted across India’s top eight cities. Bengaluru also captured 41% of GCC-oriented leasing activity, showing why the city continues attracting long-term careers, founders, global teams and families planning deeper roots. </br>
        </p>
        <p>
          Airport access also matters. In FY 2025–26, Bengaluru Airport handled 44.47 million passengers, with international traffic rising nearly 24% YoY.</br>
          For returning families, schools, hospitals, commute, parents, airport access, gated communities and social infrastructure become the real filters. Central Bengaluru offers legacy and healthcare access. Indiranagar and Old Airport Road offer lifestyle and city depth. Whitefield and ORR serve tech-linked families. Hebbal, Jakkur and North Bengaluru work for airport access, larger homes and future growth.</br>
          CircledPlus helps Global Indians shortlist homes based on family readiness, commute logic, school access, healthcare comfort and long-term lifestyle fit.</br>
          Returning to Bengaluru is not just about buying a house.</br>
          It is about choosing the base for your next chapter.
        </p>
      `
    },
    {
      category: 'Community',
      image: 'assets/img/article5.jpg',
      title: 'From Dubai, London, Singapore, or the US: How NRI Buyers Think About Bengaluru Real Estate Differently',
      desc: 'What matters beyond amenities.',
      content: `
        <p>
          Not every NRI buyer looks at Bengaluru real estate the same way.</br>
          A buyer in Dubai may think about quick travel, family proximity and business liquidity. A buyer in the US or Canada may think about a long-term return plan, RSU-led wealth and Bengaluru’s technology depth. A buyer in London may value legacy, stability and a family asset in India. A Singapore-based buyer may look at Bengaluru as a portfolio diversification and wealth-preservation decision. Australia-based buyers often think about return planning, family lifestyle, schools, hospitals and retirement readiness.</br>
          That is why a standard NRI property guide is not enough.</br>
          The data supports this shift. India received USD 135.4 billion in remittances in FY25, remaining the world’s largest recipient of remittances. The source of this capital is also changing. The US contributed 27.7% of inward remittances in 2023–24, followed by the UAE at 19.2% and the UK at 10.8%.</br>
          For Bengaluru, this matters. The city is not just a hometown market anymore. It is a wealth market, a return market and a confidence market.</br>
        </p>
        <p>
          A Dubai buyer, London buyer, Singapore buyer and San Francisco buyer may have the same budget, but they may need completely different homes. One may need airport access. One may need a legacy neighbourhood. One may need rental resilience. One may need a future-ready gated community.</br>
          CircledPlus helps Global Indians evaluate Bengaluru luxury homes through a preference-led, advisory-backed process based on where they live today, why they are buying and how they plan to use the home.</br>
          NRI real estate is not just about buying from abroad.</br>
          It is about buying with the right intent.
        </p>
      `
    },
    {
      category: 'Lifestyle',
      image: 'assets/img/article6.jpg',
      title: 'How NRIs Should Evaluate Luxury Communities in Bengaluru',
      desc: 'Lifestyle, wealth and future planning.',
      content: `
        <p>
          For NRIs buying luxury homes in Bengaluru, the real question is not just, “Which apartment should I buy?”</br>
          It is, “Which community will still feel premium ten years from now?”</br>
          That distinction matters because Bengaluru’s luxury market is expanding quickly. In Q1 2026, the city recorded 12,664 new residential launches. East Bengaluru accounted for 57% of launch activity, North Bengaluru contributed 38%, and high-end and luxury homes formed 68% of new launches. Premium buyers now have more choice than before, but more choice also means more risk of choosing the wrong community.</br>
          A luxury community is not defined by a clubhouse or brochure. It is defined by how well it lives, operates, ages and protects value.</br>
          NRIs should evaluate builder quality, RERA status, delivery record, construction quality and handover transparency. Privacy and density are equally important: units per acre, tower spacing, lift ratios, parking planning, clubhouse load and open-space design can change the entire living experience.</br>
        </p>
        <p>
          Resident profile also matters. The mix of founders, CXOs, business families, senior professionals, expats, tenants and long-term owners influences safety, culture, networking and comfort. Amenities should be judged by daily usability, maintenance and accessibility, not quantity.</br>
          Maintenance is the long-term test. Facility management, sinking fund, security, landscaping, water management, power backup and residents’ association maturity all affect lifestyle value and resale appeal.</br>
          Bengaluru’s office and GCC ecosystem supports premium demand, with 9.2 million sq ft of office transactions and 41% GCC-oriented leasing activity in Q1 2026. But liquidity still depends on lasting community credibility.</br>
          CircledPlus helps Global Indians evaluate luxury communities based on builder quality, privacy, density, resident profile, maintenance strength and long-term liveability.</br>
          The apartment creates the first impression.</br>
          The community decides the long-term value.
        </p>
      `
    },
    {
      category: 'Lifestyle',
      image: 'assets/img/article7.jpg',
      title: 'How the NRI Buying Journey Works with Circled Plus',
      desc: 'Lifestyle, wealth and future planning.',
      content: `
        <p>
          Remote property buying feels risky when the process is unclear. For NRIs, the real concern is not only “Can I buy from abroad?” It is “Can I trust what I am seeing?”</br>
          That is where a guided journey matters.</br>
          Bengaluru recorded 12,664 residential launches in Q1 2026, with 68% coming from high-end and luxury segments. East Bengaluru accounted for 57% of launches, while North Bengaluru contributed 38%. For an overseas buyer, this means more options — but also more noise.</br>
          Circled Plus reduces that noise through a simple, advisory-led process.</br>
        </p>
        <div>
            <b>Step 1: Share your requirement</b>
            <p>Tell us your budget, preferred location, buying timeline and purpose of purchase.</p>
        </div>
        <div>
            <b>Step 2: Get a curated shortlist</b>
            <p>We filter relevant options across private and premium inventory based on your intent.</p>
        </div>
        <div>
            <b>Step 3: Review virtually</b>
            <p>You receive videos, walkthroughs, micro-market context, advisor inputs and practical feedback.</p>
        </div>
        <div>
            <b>Step 4: Shortlist and schedule visits</b>
            <p>Visit when you are in India, or have a trusted family member inspect the homes.</p>
        </div>
        <div>
            <b>Step 5: Move ahead with advisory support</b>
            <p>Get support around negotiation, documentation coordination and transaction guidance.</p>
        </div>
        <p>
          For NRIs, confidence does not come from browsing hundreds of listings.</br>
          It comes from a clear process, verified context and the right homes reaching you first.
        </p>
      `
    },
    {
      category: 'Lifestyle',
      image: 'assets/img/article8.jpg',
      title: 'Why Global Indians Continue to Look at Bengaluru',
      desc: 'Lifestyle, wealth and future planning.',
      content: `
        <p>
          For Global Indians, Bengaluru is no longer just a hometown market. It is a lifestyle, wealth and future-planning market.</br>
          The first reason is lifestyle upgrade. Bengaluru gives NRIs access to larger homes, gated communities, premium neighbourhoods and family infrastructure that may feel harder to access in global cities. For families returning from the US, UK, Gulf, Singapore, Canada or Australia, this matters because the home is not only an asset — it becomes daily life infrastructure.</br>
        </p>
        <p>
          The second reason is long-term asset creation. Bengaluru continues to attract wealth from technology, startups, founders, senior professionals and Global Capability Centres. In Q1 2026, Bengaluru recorded 9.2 million sq ft of office transactions and captured 41% of GCC-oriented leasing activity across India’s leading cities. This demand creates a strong base of professionals who support premium housing, rental demand and long-term market confidence.</br>
          The third reason is roots and future planning. For many NRIs, a Bengaluru home is not only about investment return. It may become a base for parents, children, retirement, business visits or eventual relocation.</br>
          Bengaluru Airport handled 44.47 million passengers in FY 2025–26, with international passenger traffic rising nearly 24% YoY. That makes the city more relevant for globally mobile Indian families.</br>
          The real reason Global Indians look at Bengaluru is simple.</br>
          It connects wealth, family and future planning in one city.
        </p>
      `
    },
    {
      category: 'Lifestyle',
      image: 'assets/img/article9.jpg',
      title: 'Different Bengaluru Locations Serve Different NRI Goals',
      desc: 'Lifestyle, wealth and future planning.',
      content: `
        <p>
          NRIs may know “Bangalore,” but buying well requires understanding Bengaluru’s micro-market logic.</br>
          The city is not one uniform real estate market. Each location serves a different buyer goal. That matters even more now because Q1 2026 launch activity shows where the city is expanding. Bengaluru recorded <b>12,664 residential launches</b>, with East Bengaluru contributing <b>57%</b> and North Bengaluru contributing <b>38%</b>. High-end and luxury homes formed <b>68%</b> of new launch activity.</br>
          <b>Central Bengaluru</b> works for scarcity, prestige, legacy addresses and prime access. It suits buyers who want long-term address value and limited supply.</br>
        </p>
        <p>
          <b>East Bengaluru</b> works for tech wealth, larger gated communities and work-life convenience. Whitefield, ORR and nearby corridors benefit from workplace access, schools and rental demand.</br>
          <b>North Bengaluru</b> works for airport access, future growth, villas, larger-format homes and long-term planning. <b>Hebbal, Jakkur and Devanahalli</b> appeal to globally mobile families.</br>
          <b>Indiranagar, Defence Colony and Old Airport Road</b> work for lifestyle, social infrastructure, premium resale demand and mature city living. These areas are especially relevant for buyers who want convenience, neighbourhood character and stronger end-user appeal.</br>
          This is where Circled Plus can add value beyond portals.</br>
          The right question is not “Which location is best?”</br>
          It is “Which location fits your goal?”</br>
          For NRIs, the best Bengaluru address is the one that matches lifestyle, liquidity, family needs and long-term intent.
        </p>
      `
    }
  ];

  // ------------------------------------------------
  // COUNTRY INSIGHTS
  // ------------------------------------------------

  countries = [
  {
    code: 'USA',
    flag: '🇺🇸',
    description: 'US-based buyers typically focus on wealth creation and future relocation planning.',
    motivation: 'Investment & Future Return',
    locations: 'Hebbal, Whitefield, Indiranagar',
    propertyType: 'Luxury Apartments',
    budget: '$500K+'
  },

  {
    code: 'UK',
    flag: '🇬🇧',
    description: 'UK-based NRIs often prioritise family roots and long-term legacy assets.',
    motivation: 'Family & Legacy',
    locations: 'Central Bengaluru, Koramangala',
    propertyType: 'Boutique Communities',
    budget: '£300K+'
  },

  {
    code: 'UAE',
    flag: '🇦🇪',
    description: 'UAE buyers typically seek rental income and capital appreciation.',
    motivation: 'Rental Returns',
    locations: 'Whitefield, North Bengaluru',
    propertyType: 'Premium Apartments',
    budget: 'AED 1.5M+'
  }
];

  selectedCountry = this.countries[0];

  // ------------------------------------------------
  // ADVISORS
  // ------------------------------------------------

  advisors = [
    {
      name: 'Suchit Nair',
      role: 'Senior Advisor',
      image: 'assets/img/suchit.png',
      description:
        'Specialises in helping NRI buyers evaluate luxury residential opportunities across Bengaluru.'
    },
    {
      name: 'Meher Javiad',
      role: 'Property Advisor',
      image: 'assets/img/meher.png',
      description:
        'Guides buyers through location selection, project evaluation and community discovery.'
    },
    {
      name: 'Kevin Irudiaraj',
      role: 'Senior Advisor',
      image: 'assets/img/kevin.png',
      description:
        'Supports international buyers with transaction planning and long-term investment strategy.'
    }
  ];

  // ------------------------------------------------
  // LEAD FORM
  // ------------------------------------------------

  enquiry = {
    name: '',
    country: '',
    budget: '',
    location: '',
    timeline: ''
  };

  submitEnquiry() {

    console.log('NRI Lead Submitted', this.enquiry);

    // call api here

    this.enquiry = {
      name: '',
      country: '',
      budget: '',
      location: '',
      timeline: ''
    };
  }

  @HostListener('window:scroll', [])
  onScroll() {
    const section = document.querySelector('.parallax-section') as HTMLElement;
    const image = document.querySelector('.parallax-img') as HTMLElement;

    if (!section || !image) return;

    const rect = section.getBoundingClientRect();

    const windowHeight = window.innerHeight;

    // Check if section is in viewport
    if (rect.top < windowHeight && rect.bottom > 0) {

      // Progress inside viewport (0 → 1)
      const progress = (windowHeight - rect.top) / (windowHeight + rect.height);

      // Apply controlled movement
      const translate = (progress - 0.8) * 100; // adjust strength here

      image.style.transform = `translateY(${translate}px) scale(1.1)`;
    }
  }

     isExpertModalOpen = false;
  isFormSubmitted = false;

  openExpertModal() {
    this.isExpertModalOpen = true;
    this.isFormSubmitted = false;
  }

  closeExpertModal() {
    this.isExpertModalOpen = false;
  }
  get f() {
    return this.expertForm.controls;
  }

  submitExpertForm(): void {
    if (this.expertForm.invalid) {
      this.expertForm.markAllAsTouched();
      return;
    }
    this._DS.talkToExpertNotifiction(this.expertForm.value).subscribe({
      next: () => {
        this.expertForm.reset();
        this.isFormSubmitted = true;
      },
      error: (err) => {
        this.isFormSubmitted = false;
      }
    });
  }

  goToKnowledgeHub() {
    this.router.navigate(['/for-nri'], {
      fragment: 'knowledgeHub'
    });
  }
}